import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StateService } from '@app/service/state/state.service';
import { RouteComponent } from '../../route.component';
import { UserService } from '@app/service/user-service/user.service';
import { HeaderService } from '@app/service/header/header.service';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { delay, filter, repeatWhen, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AtRoute, DeRoute } from '@assets/val/route-constants';

@Component({
  selector: 'app-de-finapi',
  templateUrl: './de-finapi.component.html',
  styleUrls: ['../../page-shared.css', './de-finapi.component.scss'],
})
export class FinAPIComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  loading = true;
  error = false;
  webFormId: string;
  navigationInProgress = false;

  ftsDiv: any;
  headScript: any;
  bodyScript: any;
  pollingSubscription: Subscription;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private location: Location
  ) {
    super(router, route, stateService, headerService);
  }

  ngOnInit() {
    const stateRoute = this.stateService.getRouteFromState(this.state);

    // Check we are suppose to be here. If so, do setup()...
    if (stateRoute === AtRoute.FINAPI || stateRoute === DeRoute.FINAPI) {
      this.setup();
    }
  }

  ngOnDestroy(): void {
    this.removeScript();
  }

  async setup(): Promise<void> {
    await this.fetchNewConnectionWebForm();
    this.loading = false;
  }

  fetchNewConnectionWebForm(): void {
    this.userService.getFinapiNewConnectionWebForm().subscribe({
      next: () => {
        this.webFormId = this.userService.webformId;
        if (!this.webFormId) {
          this.error = true;
          this.loading = false;
        } else {
          this.loadFinApiHeadScript(this.webFormId);
        }
      },
      error: (e: any) => {
        console.log(e);
        this.error = true;
        this.loading = false;
      },
    });
  }

  removeScript() {
    this.headScript?.bodyScript?.remove();
    this.headScript?.remove();
  }

  async loadFinApiHeadScript(token: string): Promise<void> {
    this.removeScript();

    this.headScript = document.createElement('script');
    this.headScript.src =
      'https://webform-dist.finapi.io/latest/dist/web-form.min.js';
    this.headScript.type = 'text/javascript';

    this.headScript.addEventListener('load', () => {
      this.bodyScript = document.createElement('script');
      this.bodyScript.type = 'text/javascript';

      this.bodyScript.textContent = `
        (async () => {
          FinApiWebForm.load(
            document.querySelector("#webFormContainer"),
            {
              token: "${token}",
              targetUrl: "${environment.finApiUrl}",
              layoutConfig: "xs",
              customerSupportUrl: "https://zaver.com/de/customer-support",
              language: "de",
            },
            {
              onLoaded: function() {
                window.dispatchEvent(new CustomEvent('loaded'));
              },
              onComplete: function() {
                window.dispatchEvent(new CustomEvent('finish'));
              },
              onFail: function() {
                window.dispatchEvent(new CustomEvent('error'));
              },
              onAbort: function() {
                window.dispatchEvent(new CustomEvent('abort'));
              },
              onLoadError: function() {
                window.dispatchEvent(new CustomEvent('error'));
              }
            }
          );
        })();
      `;

      document.getElementsByTagName('body')[0].appendChild(this.bodyScript);
    });

    document.getElementsByTagName('head')[0].appendChild(this.headScript);
  }

  @HostListener('window:error', ['$event'])
  logError(e: CustomEvent) {
    this.fetchNewConnectionWebForm();
  }

  @HostListener('window:finish')
  finishSuccessfulWizard() {
    this.loading = true;
    this.pollForFinapiReport();
  }

  @HostListener('window:abort')
  abortUnsuccessfulWizard() {
    this.fetchNewConnectionWebForm();
  }

  @HostListener('window:loaded')
  loaded() {
    this.loading = false;
  }

  async handleCtaClick(): Promise<void> {
    this.navigationInProgress = true;
    this.location.back();
    this.navigationInProgress = false;
  }

  pollForFinapiReport(): void {
    this.pollingSubscription = this.userService
      .pollForFinapiReport(this.webFormId)
      .pipe(
        repeatWhen((obs) => obs.pipe(delay(1000))),
        take(120),
        filter((res) => !res)
      )
      .subscribe(() => this.handleUpdate());
  }

  private stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  async handleUpdate() {
    this.stopPolling();
    await this.userService.fetchFinApiReport(this.webFormId);
    this.navigate();
  }
}
